import { AxiosResponse } from 'axios';
import { EducationType } from '@/components/CandidateEducationItem/CandidateEducationItem.types';
import { WorkExperienceType } from '@/components/WorkExperience.types';
import { getActiveOrgId } from '@/lib/activeOrg';
import { mapProfileToPerson } from '@/lib/mappers';
import { supabase } from '@/lib/supabase';
import { asResponse, paginationRange, buildPaginatedResponse } from '@/lib/supabase-helpers';
import { ResponseWithPagination, TechStackType, ExpertiseType } from '@/types';
import {
  CandidateStagesResponse,
  TotalCandidatesByTechStack,
  AvgTimeToAcceptanceOrRejectionByOwnerResponse,
  AvgTimeByTechStackData,
  PersonType,
  PaginationParams,
  SortParams,
  TopicType,
} from '@/types/api';

// ---------------------------------------------------------------------------
// Helper: map a DB work_experiences row → WorkExperienceType
// ---------------------------------------------------------------------------
function parseMonthFromDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return String(d.getMonth() + 1).padStart(2, '0');
}

function parseYearFromDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return String(d.getFullYear());
}

function mapWorkExperience(r: any): WorkExperienceType {
  return {
    id: r.id,
    company: r.company ?? '',
    title: r.title ?? '',
    location: r.location ?? '',
    description: r.description ?? '',
    techs: [],
    isCurrentWork: r.is_current_work ?? false,
    startMonth: r.start_month ?? '',
    startYear: r.start_year != null ? String(r.start_year) : '',
    endMonth: r.end_month ?? '',
    endYear: r.end_year != null ? String(r.end_year) : '',
  };
}

// ---------------------------------------------------------------------------
// Helper: map a DB educations row → EducationType (from CandidateEducationItem)
// ---------------------------------------------------------------------------
function mapEducation(r: any): EducationType {
  return {
    id: r.id,
    university: r.university ?? '',
    degree: r.degree ?? '',
    duration: '',
    startMonth: r.start_month ?? '',
    startYear: r.start_year != null ? String(r.start_year) : '',
    endMonth: r.end_month ?? '',
    endYear: r.end_year != null ? String(r.end_year) : '',
    currentlyPursuing: false,
  };
}

// ---------------------------------------------------------------------------
// Helper: convert camelCase candidate fields to snake_case for DB writes.
// ---------------------------------------------------------------------------
function candidateToRow(fields: Record<string, any>): Record<string, any> {
  const mapping: Record<string, string> = {
    firstName: 'first_name',
    lastName: 'last_name',
    pictureUrl: 'picture_url',
    linkedinUrl: 'linkedin_url',
    githubUrl: 'github_url',
    developerType: 'developer_type',
    englishLevel: 'english_level',
    salaryExpectation: 'salary_expectation',
    referredBy: 'referred_by',
    rating: 'overall_rating',
    communicationSkillsRating: 'communication_rating',
    softSkillsRating: 'soft_skills_rating',
    technicalSkillsRating: 'technical_rating',
    addressLine1: 'address_line1',
    addressLine2: 'address_line2',
    zipCode: 'zip_code',
    mobilePhone: 'phone',
    emergencyContactName: 'emergency_contact_name',
    emergencyContactPhone: 'emergency_contact_phone',
    emergencyContactEmail: 'emergency_contact_email',
    emergencyContactRelationship: 'emergency_contact_relationship',
    employeeWorkStatus: 'employee_work_status',
    workEmail: 'work_email',
    ownerId: 'owner_id',
    isTalentPool: 'is_talent_pool',
    trioProfileUrl: 'trio_profile_url',
    hasFintechExperience: 'has_fintech_experience',
  };

  const row: Record<string, any> = {};
  for (const [key, value] of Object.entries(fields)) {
    // Skip read-only / computed fields that don't belong in the profiles table
    if (
      [
        'id',
        'mainTechStack',
        'skills',
        'createdAt',
        'updatedAt',
        'deletedAt',
        'candidateOwner',
        'owner',
      ].includes(key)
    ) {
      continue;
    }
    // Skip undefined values — only send fields that have actual data
    if (value === undefined) continue;
    const dbCol = mapping[key] ?? key;
    row[dbCol] = value;
  }
  return row;
}

// ---------------------------------------------------------------------------
// Exported types (unchanged)
// ---------------------------------------------------------------------------

export type Candidate = PersonType;

export type Profile = {
  workExperiences: WorkExperienceType[];
  expertises: ExpertiseType[];
  educations: EducationType[];
};

export type EditableCandidate = Omit<Candidate, 'mainTechStack'> & { mainTechStack: string[] };

export type CandidateResponse = {
  candidate: Candidate;
};

export type CandidateProfileResponse = {
  profile: Profile;
};

export type CandidateParams = {
  candidate: Partial<Candidate>;
};

export type PartialCandidateToSubmit = {
  candidate: Partial<Candidate>;
};

export type CandidateToSubmit = Partial<Candidate>;

export type AttachCandidateAndTechStackType = {
  candidateId: string;
  techs: string[];
};

export type NewCandidateResponse = {
  label: string;
  persons: Candidate[];
};

export type CandidatesResponse = ResponseWithPagination<Candidate[]>;

export type NewTopicParams = {
  id: string;
  stageId: string;
  name: string;
};

export type NewNoteParams = {
  id: string;
  topicId: string;
  text: string;
};

export type UpdateNoteParams = NewNoteParams & {
  noteId: string;
};

export type DeleteNoteParams = Omit<UpdateNoteParams, 'text'>;
export type DeleteCandidateParams = {
  id: number | string;
};

export type ExperienceParamsType = {
  personId: string;
  experienceId?: string;
};

export type ExperienceResponseType = {
  workExperience: WorkExperienceType;
};

export type MutateExperienceParams = ExperienceParamsType & Partial<ExperienceResponseType>;

export type GetAllStatusType = {
  status: string;
  color: string;
  stage: string;
};

export type ExpertiseParamsType = {
  personId?: string;
  expertiseId?: string;
};

export type ExpertiseResponseType = ExpertiseType;

export type MutateExpertiseParams = ExpertiseParamsType & Partial<ExpertiseResponseType>;

export type UploadCandidateProfileType = {
  personId: string;
  imageUrl?: string;
  imageFile?: FormData;
};

export type UpdateTopicName = Pick<NewNoteParams, 'id' | 'topicId'> & { name: string };
export type DeleteTopicType = Pick<NewNoteParams, 'id' | 'topicId'>;

type FiltersParams = {
  filters: string | null;
};

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

const getAllCandidates = async ({
  take,
  page,
}: PaginationParams & Partial<SortParams>): Promise<AxiosResponse<CandidatesResponse>> => {
  const [from, to] = paginationRange(page, take);

  const {
    data: rows,
    error,
    count,
  } = await supabase
    .from('profiles')
    .select('*', { count: 'exact' })
    .in('profile_type', ['candidate', 'both'])
    .is('deleted_at', null)
    .range(from, to)
    .order('created_at', { ascending: false });

  if (error) throw error;

  const total = count ?? 0;

  return asResponse<CandidatesResponse>(
    buildPaginatedResponse((rows ?? []).map(mapProfileToPerson), total, page, take) as any
  );
};

const getFilteredCandidates = async ({
  take,
  page,
  column,
  order,
  filters,
}: PaginationParams & SortParams & FiltersParams): Promise<AxiosResponse<CandidatesResponse>> => {
  const [from, to] = paginationRange(page, take);

  let query = supabase
    .from('profiles')
    .select('*', { count: 'exact' })
    .in('profile_type', ['candidate', 'both'])
    .is('deleted_at', null);

  // Apply simple key=value filters if provided
  if (filters) {
    try {
      const parsed = JSON.parse(filters);
      if (typeof parsed === 'object' && parsed !== null) {
        for (const [key, value] of Object.entries(parsed)) {
          if (value !== null && value !== undefined && value !== '') {
            query = query.eq(key, value);
          }
        }
      }
    } catch {
      // If filters is not JSON, treat it as a status filter for backward compat
      query = query.eq('status', filters);
    }
  }

  const columnMapping: Record<string, string> = {
    firstName: 'first_name',
    lastName: 'last_name',
    createdAt: 'created_at',
    updatedAt: 'updated_at',
  };
  const dbColumn = columnMapping[column] ?? column;
  query = query.order(dbColumn, { ascending: order === 'asc' }).range(from, to);

  const { data: rows, error, count } = await query;

  if (error) throw error;

  const total = count ?? 0;

  return asResponse<CandidatesResponse>(
    buildPaginatedResponse((rows ?? []).map(mapProfileToPerson), total, page, take) as any
  );
};

const searchCandidate = async (term: string): Promise<AxiosResponse<Candidate[]>> => {
  const { data: rows, error } = await supabase
    .from('profiles')
    .select('*')
    .in('profile_type', ['candidate', 'both'])
    .is('deleted_at', null)
    .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%,email.ilike.%${term}%`)
    .limit(50);

  if (error) throw error;
  return asResponse<Candidate[]>((rows ?? []).map(mapProfileToPerson));
};

const searchCandidatesBySkill = async (skillName: string): Promise<AxiosResponse<Candidate[]>> => {
  // Find profiles that have the given skill via profile_skills + skills tables
  const { data: skillRows, error: skillErr } = await supabase
    .from('skills')
    .select('id')
    .ilike('name', skillName)
    .limit(1);

  if (skillErr) throw skillErr;
  if (!skillRows || skillRows.length === 0) return asResponse<Candidate[]>([]);

  const skillId = skillRows[0].id;

  const { data: profileSkills, error: psErr } = await supabase
    .from('profile_skills')
    .select('profile_id')
    .eq('skill_id', skillId);

  if (psErr) throw psErr;
  if (!profileSkills || profileSkills.length === 0) return asResponse<Candidate[]>([]);

  const profileIds = profileSkills.map((ps: any) => ps.profile_id);

  const { data: rows, error } = await supabase
    .from('profiles')
    .select('*')
    .in('id', profileIds)
    .in('profile_type', ['candidate', 'both'])
    .is('deleted_at', null);

  if (error) throw error;
  return asResponse<Candidate[]>((rows ?? []).map(mapProfileToPerson));
};

const getCandidateByID = async (candidateID: string): Promise<AxiosResponse<CandidateResponse>> => {
  const { data: row, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', candidateID)
    .single();

  if (error) throw error;
  return asResponse<CandidateResponse>({ candidate: mapProfileToPerson(row) });
};

const createCandidate = async (
  candidate: CandidateToSubmit
): Promise<AxiosResponse<CandidateResponse>> => {
  const row = candidateToRow(candidate as Record<string, any>);
  row.profile_type = 'candidate';

  const orgId = getActiveOrgId();
  if (orgId) row.organization_id = orgId;

  const { data: inserted, error } = await supabase.from('profiles').insert(row).select().single();

  if (error) throw error;
  return asResponse<CandidateResponse>({ candidate: mapProfileToPerson(inserted) });
};

const attechCandidateAndTechId = async ({
  candidateId,
  techs,
}: AttachCandidateAndTechStackType): Promise<AxiosResponse<void>> => {
  const rows = techs.map((skillId) => ({ profile_id: candidateId, skill_id: skillId }));
  const { error } = await supabase.from('profile_skills').insert(rows);
  if (error) throw error;
  return asResponse<void>(undefined as void);
};

const updatedLinkCandidateAndTechId = async ({
  candidateId,
  techs,
}: AttachCandidateAndTechStackType): Promise<AxiosResponse<void>> => {
  // Remove existing links then insert new ones
  const { error: delErr } = await supabase
    .from('profile_skills')
    .delete()
    .eq('profile_id', candidateId);
  if (delErr) throw delErr;

  if (techs.length > 0) {
    const rows = techs.map((skillId) => ({ profile_id: candidateId, skill_id: skillId }));
    const { error: insErr } = await supabase.from('profile_skills').insert(rows);
    if (insErr) throw insErr;
  }

  return asResponse<void>(undefined as void);
};

const updateCandidateById = async ({
  candidate: { id, ...fields },
}: CandidateParams): Promise<AxiosResponse<CandidateResponse>> => {
  const row = candidateToRow(fields as Record<string, any>);
  const { data: updated, error } = await supabase
    .from('profiles')
    .update(row)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return asResponse<CandidateResponse>({ candidate: mapProfileToPerson(updated) });
};

const updateCandidateIndividuallyById = async ({
  candidate: { id, ...candidateToUpdate },
}: PartialCandidateToSubmit): Promise<AxiosResponse<CandidateResponse>> => {
  const row = candidateToRow(candidateToUpdate as Record<string, any>);
  const { data: updated, error } = await supabase
    .from('profiles')
    .update(row)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return asResponse<CandidateResponse>({ candidate: mapProfileToPerson(updated) });
};

const updateCandidatePersonalInfo = async ({
  candidate: candidateToUpdate,
}: {
  candidate: Partial<Candidate>;
}): Promise<AxiosResponse<CandidateResponse>> => {
  // "me" endpoint — get the current user's profile via auth_user_id
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Look up the profile ID from auth_user_id (they are different UUIDs)
  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('auth_user_id', user.id)
    .single();
  if (!profile) throw new Error('Profile not found');

  const row = candidateToRow(candidateToUpdate as Record<string, any>);
  const { data: updated, error } = await supabase
    .from('profiles')
    .update(row)
    .eq('id', profile.id)
    .select()
    .single();

  if (error) throw error;
  return asResponse<CandidateResponse>({ candidate: mapProfileToPerson(updated) });
};

const getAllTechsStacks = async (): Promise<AxiosResponse<TechStackType[]>> => {
  const { data: rows, error } = await supabase
    .from('skills')
    .select('*')
    .order('name', { ascending: true });

  if (error) throw error;

  const mapped = (rows ?? []).map((r: any) => ({
    id: r.id,
    name: r.name,
    isActive: r.is_active ?? true,
    createdAt: r.created_at ?? '',
    updatedAt: r.updated_at ?? '',
  }));

  return asResponse<TechStackType[]>(mapped);
};

const getTotalCandidatesByTechStack = async (): Promise<
  AxiosResponse<TotalCandidatesByTechStack[]>
> => {
  const { data, error } = await supabase.rpc('rpc_total_candidates_by_tech_stack');
  if (error) throw error;
  return asResponse<TotalCandidatesByTechStack[]>(data || []);
};

const getCandidateStages = async (id: string): Promise<AxiosResponse<CandidateStagesResponse>> => {
  // person_stages is a lookup table; candidate's stages come via stage_topics
  const { data: stages, error } = await supabase
    .from('person_stages')
    .select('*')
    .order('sequence', { ascending: true });

  if (error) throw error;

  const stagesWithTopics = await Promise.all(
    (stages ?? []).map(async (stage: any) => {
      const { data: topics, error: topicErr } = await supabase
        .from('stage_topics')
        .select('*')
        .eq('stage_id', stage.id)
        .eq('profile_id', id)
        .order('created_at', { ascending: true });

      if (topicErr) throw topicErr;

      const topicsWithNotes = await Promise.all(
        (topics ?? []).map(async (topic: any) => {
          const { data: notes, error: noteErr } = await supabase
            .from('notes')
            .select('*, author:profiles!author_id(id, first_name, last_name, email)')
            .eq('stage_topic_id', topic.id)
            .order('created_at', { ascending: true });

          if (noteErr) throw noteErr;

          return {
            id: topic.id,
            name: topic.name,
            stageId: stage.id,
            candidateId: id,
            isDefault: topic.is_default ?? false,
            createdAt: topic.created_at ?? '',
            updatedAt: topic.updated_at ?? '',
            notes: (notes ?? []).map((n: any) => ({
              id: n.id,
              text: n.text,
              createdAt: n.created_at ?? '',
              updatedAt: n.updated_at ?? '',
              author: n.author
                ? {
                    id: n.author.id,
                    name: `${n.author.first_name ?? ''} ${n.author.last_name ?? ''}`.trim(),
                    email: n.author.email ?? '',
                  }
                : {},
            })),
          };
        })
      );

      return {
        id: stage.id,
        name: stage.name,
        createdAt: stage.created_at ?? '',
        updatedAt: stage.updated_at ?? '',
        topics: topicsWithNotes,
      };
    })
  );

  return asResponse<CandidateStagesResponse>({ stages: stagesWithTopics });
};

const getCurrentCandidates = async (): Promise<AxiosResponse<Record<string, Candidate[]>>> => {
  const { data: rows, error } = await supabase
    .from('profiles')
    .select('*')
    .in('profile_type', ['candidate', 'both'])
    .is('deleted_at', null)
    .not('status', 'is', null);

  if (error) throw error;

  const grouped: Record<string, Candidate[]> = {};
  for (const row of rows ?? []) {
    const person = mapProfileToPerson(row);
    const status = person.status ?? 'Unknown';
    if (!grouped[status]) grouped[status] = [];
    grouped[status].push(person);
  }

  return asResponse<Record<string, Candidate[]>>(grouped);
};

const getNewCandidates = async (): Promise<AxiosResponse<NewCandidateResponse[]>> => {
  // Return candidates created in the last 7 days, grouped by date label
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const { data: rows, error } = await supabase
    .from('profiles')
    .select('*')
    .in('profile_type', ['candidate', 'both'])
    .is('deleted_at', null)
    .gte('created_at', sevenDaysAgo.toISOString())
    .order('created_at', { ascending: false });

  if (error) throw error;

  const grouped: Record<string, Candidate[]> = {};
  for (const row of rows ?? []) {
    const person = mapProfileToPerson(row);
    const dateLabel = new Date(person.createdAt).toLocaleDateString();
    if (!grouped[dateLabel]) grouped[dateLabel] = [];
    grouped[dateLabel].push(person);
  }

  const result: NewCandidateResponse[] = Object.entries(grouped).map(([label, persons]) => ({
    label,
    persons,
  }));

  return asResponse<NewCandidateResponse[]>(result);
};

const getAvgTimeToAcceptanceOrRejectionByOwner = async (): Promise<
  AxiosResponse<AvgTimeToAcceptanceOrRejectionByOwnerResponse[]>
> => {
  const { data, error } = await supabase.rpc('rpc_avg_time_by_owner');
  if (error) throw error;
  return asResponse<AvgTimeToAcceptanceOrRejectionByOwnerResponse[]>(data || []);
};

const getAvgTimeToAcceptanceOrRejectionByTechStack = async (): Promise<
  AxiosResponse<AvgTimeByTechStackData[]>
> => {
  const { data, error } = await supabase.rpc('rpc_avg_time_by_tech_stack');
  if (error) throw error;
  return asResponse<AvgTimeByTechStackData[]>(data || []);
};

const addNewTopic = async ({
  id,
  stageId,
  name,
}: NewTopicParams): Promise<AxiosResponse<TopicType>> => {
  const { data: inserted, error } = await supabase
    .from('stage_topics')
    .insert({ stage_id: stageId, profile_id: id, name })
    .select()
    .single();

  if (error) throw error;

  return asResponse<TopicType>({
    id: inserted.id,
    name: inserted.name,
    stageId: inserted.stage_id,
    isDefault: inserted.is_default ?? false,
    notes: [],
    createdAt: inserted.created_at ?? '',
    updatedAt: inserted.updated_at ?? '',
  });
};

const addNewNoteToTheTopic = async ({
  id,
  topicId,
  text,
}: NewNoteParams): Promise<AxiosResponse<CandidateStagesResponse>> => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  const authorId = user?.id ?? null;

  const { error } = await supabase
    .from('notes')
    .insert({ stage_topic_id: topicId, profile_id: id, author_id: authorId, text });

  if (error) throw error;

  // Return refreshed stages
  return getCandidateStages(id);
};

const updateNoteToTheTopic = async ({
  id,
  topicId: _topicId,
  text,
  noteId,
}: UpdateNoteParams): Promise<AxiosResponse<CandidateStagesResponse>> => {
  const { error } = await supabase.from('notes').update({ text }).eq('id', noteId);

  if (error) throw error;
  return getCandidateStages(id);
};

const updateTopicName = async ({
  id,
  topicId,
  name,
}: UpdateTopicName): Promise<AxiosResponse<CandidateStagesResponse>> => {
  const { error } = await supabase.from('stage_topics').update({ name }).eq('id', topicId);

  if (error) throw error;
  return getCandidateStages(id);
};

const deleteTopic = async ({
  id,
  topicId,
}: DeleteTopicType): Promise<AxiosResponse<CandidateStagesResponse>> => {
  const { error } = await supabase.from('stage_topics').delete().eq('id', topicId);

  if (error) throw error;
  return getCandidateStages(id);
};

const deleteNoteToTheTopic = async ({
  id,
  topicId: _topicId,
  noteId,
}: DeleteNoteParams): Promise<AxiosResponse<CandidateStagesResponse>> => {
  const { error } = await supabase.from('notes').delete().eq('id', noteId);

  if (error) throw error;
  return getCandidateStages(id);
};

const deleteCandidate = async ({ id }: { id: number | string }): Promise<AxiosResponse<string>> => {
  // Soft delete
  const { error } = await supabase
    .from('profiles')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
  return asResponse<string>('Candidate deleted successfully');
};

const addNewExperience = async ({
  personId,
  workExperience,
}: MutateExperienceParams): Promise<AxiosResponse<ExperienceResponseType>> => {
  const row = {
    profile_id: personId,
    company: workExperience?.company ?? '',
    title: workExperience?.title ?? '',
    start_month: workExperience?.startMonth || null,
    start_year: workExperience?.startYear ? Number(workExperience.startYear) : null,
    end_month: workExperience?.endMonth || null,
    end_year: workExperience?.endYear ? Number(workExperience.endYear) : null,
    description: workExperience?.description ?? '',
    is_current_work: workExperience?.isCurrentWork ?? false,
  };

  const { data: inserted, error } = await supabase
    .from('work_experiences')
    .insert(row)
    .select()
    .single();

  if (error) throw error;

  return asResponse<ExperienceResponseType>({
    workExperience: mapWorkExperience(inserted),
  });
};

const updateExperience = async ({
  personId: _personId,
  experienceId,
  workExperience,
}: MutateExperienceParams): Promise<AxiosResponse<ExperienceResponseType>> => {
  const row: Record<string, any> = {};
  if (workExperience?.company !== undefined) row.company = workExperience.company;
  if (workExperience?.title !== undefined) row.title = workExperience.title;
  if (workExperience?.startMonth !== undefined) row.start_month = workExperience.startMonth || null;
  if (workExperience?.startYear !== undefined)
    row.start_year = workExperience.startYear ? Number(workExperience.startYear) : null;
  if (workExperience?.endMonth !== undefined) row.end_month = workExperience.endMonth || null;
  if (workExperience?.endYear !== undefined)
    row.end_year = workExperience.endYear ? Number(workExperience.endYear) : null;
  if (workExperience?.description !== undefined) row.description = workExperience.description;
  if (workExperience?.isCurrentWork !== undefined)
    row.is_current_work = workExperience.isCurrentWork;

  const { data: updated, error } = await supabase
    .from('work_experiences')
    .update(row)
    .eq('id', experienceId)
    .select()
    .single();

  if (error) throw error;

  return asResponse<ExperienceResponseType>({
    workExperience: mapWorkExperience(updated),
  });
};

const deleteExperience = async ({
  personId: _personId,
  experienceId,
}: ExperienceParamsType): Promise<AxiosResponse<null>> => {
  const { error } = await supabase.from('work_experiences').delete().eq('id', experienceId);

  if (error) throw error;
  return asResponse<null>(null);
};

const getProfileByID = async (
  personId: string
): Promise<AxiosResponse<CandidateProfileResponse>> => {
  // Fetch work experiences
  const { data: expRows, error: expErr } = await supabase
    .from('work_experiences')
    .select('*')
    .eq('profile_id', personId)
    .order('start_year', { ascending: false });
  if (expErr) throw expErr;

  const workExperiences: WorkExperienceType[] = (expRows ?? []).map(mapWorkExperience);

  // Fetch expertises (profile_skills + skills)
  const { data: skillRows, error: skillErr } = await supabase
    .from('profile_skills')
    .select('*, skill:skills(*)')
    .eq('profile_id', personId);
  if (skillErr) throw skillErr;

  const expertises: ExpertiseType[] = (skillRows ?? []).map((r: any) => ({
    id: r.id,
    experience: r.years_experience?.toString() ?? '',
    description: '',
    skill: {
      id: r.skill?.id ?? r.skill_id,
      name: r.skill?.name ?? '',
      createdAt: r.skill?.created_at,
      updatedAt: r.skill?.updated_at,
    },
    subSkills: [],
    tags: [],
    personSkillTags: [],
    isMainStack: r.is_main_stack ?? false,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  })) as ExpertiseType[];

  // Fetch educations
  const { data: eduRows, error: eduErr } = await supabase
    .from('educations')
    .select('*')
    .eq('profile_id', personId)
    .order('start_year', { ascending: false });
  if (eduErr) throw eduErr;

  const educations: EducationType[] = (eduRows ?? []).map((r: any) => mapEducation(r));

  return asResponse<CandidateProfileResponse>({
    profile: { workExperiences, expertises, educations },
  });
};

const getAllStatus = async (): Promise<AxiosResponse<GetAllStatusType[]>> => {
  const { data: rows, error } = await supabase
    .from('statuses')
    .select('*, person_stages(name, color, sequence)')
    .order('stage_id');

  if (error) throw error;

  const mapped = (rows ?? [])
    .sort((a: any, b: any) => (a.person_stages?.sequence ?? 0) - (b.person_stages?.sequence ?? 0))
    .map((r: any) => ({
      status: r.value,
      color: r.person_stages?.color ?? '',
      stage: r.person_stages?.name ?? '',
    }));

  return asResponse<GetAllStatusType[]>(mapped);
};

const addNewExpertise = async ({
  personId,
  ...expertise
}: MutateExpertiseParams): Promise<AxiosResponse<Array<ExpertiseResponseType>>> => {
  const row = {
    profile_id: personId,
    skill_id: expertise.skill?.id ?? expertise.id,
    years_experience: expertise.experience ? parseInt(expertise.experience, 10) : null,
    proficiency_level: null,
    is_main_stack: expertise.isMainStack ?? false,
  };

  const { data: inserted, error } = await supabase
    .from('profile_skills')
    .insert(row)
    .select('*, skill:skills(*)')
    .single();

  if (error) throw error;

  const mapped: ExpertiseResponseType = {
    id: inserted.id,
    experience: inserted.years_experience?.toString() ?? '',
    description: '',
    skill: {
      id: inserted.skill?.id ?? inserted.skill_id,
      name: inserted.skill?.name ?? '',
      createdAt: inserted.skill?.created_at,
      updatedAt: inserted.skill?.updated_at,
    },
    subSkills: [],
    tags: [],
    personSkillTags: [],
    isMainStack: inserted.is_main_stack ?? false,
    createdAt: inserted.created_at,
    updatedAt: inserted.updated_at,
  } as ExpertiseResponseType;

  return asResponse<Array<ExpertiseResponseType>>([mapped]);
};

const updateExpertise = async ({
  personId: _personId,
  expertiseId,
  ...expertise
}: MutateExpertiseParams): Promise<AxiosResponse<Array<ExpertiseResponseType>>> => {
  const row: Record<string, any> = {};
  if (expertise.experience !== undefined) row.years_experience = parseInt(expertise.experience, 10);
  if (expertise.isMainStack !== undefined) row.is_main_stack = expertise.isMainStack;
  if (expertise.skill?.id !== undefined) row.skill_id = expertise.skill.id;

  const { data: updated, error } = await supabase
    .from('profile_skills')
    .update(row)
    .eq('id', expertiseId)
    .select('*, skill:skills(*)')
    .single();

  if (error) throw error;

  const mapped: ExpertiseResponseType = {
    id: updated.id,
    experience: updated.years_experience?.toString() ?? '',
    description: '',
    skill: {
      id: updated.skill?.id ?? updated.skill_id,
      name: updated.skill?.name ?? '',
      createdAt: updated.skill?.created_at,
      updatedAt: updated.skill?.updated_at,
    },
    subSkills: [],
    tags: [],
    personSkillTags: [],
    isMainStack: updated.is_main_stack ?? false,
    createdAt: updated.created_at,
    updatedAt: updated.updated_at,
  } as ExpertiseResponseType;

  return asResponse<Array<ExpertiseResponseType>>([mapped]);
};

const deleteExpertise = async ({
  expertiseId,
}: ExpertiseParamsType): Promise<AxiosResponse<null>> => {
  const { error } = await supabase.from('profile_skills').delete().eq('id', expertiseId);

  if (error) throw error;
  return asResponse<null>(null);
};

// ---------------------------------------------------------------------------
// Helper: upload a profile picture to Supabase Storage (avatars bucket)
// Returns the public URL of the uploaded image.
// ---------------------------------------------------------------------------
const uploadAvatarFile = async (
  profileId: string,
  imageFile?: FormData,
  imageUrl?: string
): Promise<string | null> => {
  // If a file was selected, upload it
  const file = imageFile?.get('imageFile') as File | null;
  if (file) {
    const ext = file.name.split('.').pop() || 'png';
    const filePath = `${profileId}/avatar.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true });
    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);

    return urlData.publicUrl;
  }

  // If only a URL was pasted, use it directly
  const pastedUrl = imageFile?.get('imageUrl') as string | null;
  if (pastedUrl) return pastedUrl;
  if (imageUrl) return imageUrl;

  return null;
};

const uploadCandidateProfilePicture = async ({
  personId,
  imageUrl,
  imageFile,
}: UploadCandidateProfileType): Promise<AxiosResponse<null>> => {
  const url = await uploadAvatarFile(personId, imageFile, imageUrl);

  const { error } = await supabase.from('profiles').update({ picture_url: url }).eq('id', personId);

  if (error) throw error;
  return asResponse<null>(null);
};

const uploadMyProfilePicture = async ({
  imageUrl,
  imageFile,
}: UploadCandidateProfileType): Promise<AxiosResponse<null>> => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Resolve the profile_id for the current user
  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('auth_user_id', user.id)
    .single();

  const profileId = profile?.id ?? user.id;
  const url = await uploadAvatarFile(profileId, imageFile, imageUrl);

  const { error } = await supabase
    .from('profiles')
    .update({ picture_url: url })
    .eq('id', profileId);

  if (error) throw error;
  return asResponse<null>(null);
};

const deleteCandidateProfilePicture = async ({
  personId,
}: UploadCandidateProfileType): Promise<AxiosResponse<null>> => {
  // Remove from storage (best-effort, ignore errors if file doesn't exist)
  await supabase.storage
    .from('avatars')
    .remove([
      `${personId}/avatar.png`,
      `${personId}/avatar.jpg`,
      `${personId}/avatar.jpeg`,
      `${personId}/avatar.webp`,
    ]);

  const { error } = await supabase
    .from('profiles')
    .update({ picture_url: null })
    .eq('id', personId);

  if (error) throw error;
  return asResponse<null>(null);
};

const deleteMyProfilePicture = async (): Promise<AxiosResponse<null>> => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('auth_user_id', user.id)
    .single();

  const profileId = profile?.id ?? user.id;
  await supabase.storage
    .from('avatars')
    .remove([
      `${profileId}/avatar.png`,
      `${profileId}/avatar.jpg`,
      `${profileId}/avatar.jpeg`,
      `${profileId}/avatar.webp`,
    ]);

  const { error } = await supabase
    .from('profiles')
    .update({ picture_url: null })
    .eq('id', profileId);

  if (error) throw error;
  return asResponse<null>(null);
};

// ---------------------------------------------------------------------------
// Default export — same shape as before
// ---------------------------------------------------------------------------

export default {
  deleteCandidateProfilePicture,
  uploadCandidateProfilePicture,
  uploadMyProfilePicture,
  deleteMyProfilePicture,
  getAllStatus,
  deleteNoteToTheTopic,
  updateNoteToTheTopic,
  addNewNoteToTheTopic,
  deleteTopic,
  updateTopicName,
  addNewTopic,
  getCandidateStages,
  getFilteredCandidates,
  updatedLinkCandidateAndTechId,
  attechCandidateAndTechId,
  getAllTechsStacks,
  getAllCandidates,
  getNewCandidates,
  getTotalCandidatesByTechStack,
  getCandidateByID,
  createCandidate,
  updateCandidateById,
  searchCandidate,
  searchCandidatesBySkill,
  updateCandidateIndividuallyById,
  updateCandidatePersonalInfo,
  deleteCandidate,
  getCurrentCandidates,
  getAvgTimeToAcceptanceOrRejectionByOwner,
  getAvgTimeToAcceptanceOrRejectionByTechStack,
  addNewExperience,
  updateExperience,
  deleteExperience,
  getProfileByID,
  addNewExpertise,
  deleteExpertise,
  updateExpertise,
};
